import { Delete } from "lucide-react"
import { useEffect, useRef } from "react"
import { Button, Col, Form, Row, InputGroup } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"
import Select from "react-select"
import { updateCustomProtocol } from '../../actions/auth/usersActions'
import Swal from "sweetalert2"

export default function ProtocolForm({state, protocoltype, handleInputChange, submit, isAddingOrEditing, isRegistering, templatesOptions = [], techniciansOptions = [], breedersOptions = [], animalsOptions = [], lotsOptions = []}) {
	const { t } = useTranslation()
	const dispatch = useDispatch()

	const getOptionsLots = (items, labelKey = 'identifier') =>
		items?.map(item => ({ value: item._id, label: item[labelKey] })) || []

	const getOptionsAnimals = (items, labelKey = 'crotal') =>
		items?.map(item => ({ value: item._id, label: item[labelKey] })) || []

	const getOptions = (items, labelKey = 'name') =>
		items?.map(item => ({ value: item._id, label: item[labelKey] })) || []

	const handleSelectChange = (field, option) => {
		handleInputChange({ target: { name: field, value: option?.value || '' } })
	}

	const handleMultiSelectChange = (field, options) => {
		handleInputChange({ target: { name: field, value: options.map(opt => opt.value) } })
	}

	const handleTaskChange = (index, field, value) => {
		const newTasks = [...(state.tasks || [])]
		newTasks[index] = {
			...newTasks[index],
			[field]: field === 'day' ? parseInt(value) : value
		}
		handleInputChange({ target: { name: 'tasks', value: newTasks } })
	}

	const addTask = () => {
		const newTasks = [...(state.tasks || []), { day: 0, step: '', assignedTo: 'technical' }]
		handleInputChange({ target: { name: 'tasks', value: newTasks } })
	}

	const removeTask = (index) => {
		const newTasks = [...(state.tasks || [])]
		newTasks.splice(index, 1)
		handleInputChange({ target: { name: 'tasks', value: newTasks } })
	}

	const originalTasksRef = useRef([])

	useEffect(() => {
		if (state.template && state.technician) {
			const currentUser = techniciansOptions.find(u => u._id === state.technician._id)
			const custom = currentUser?.customProtocols?.find(p => p.template === state.template._id)
			if (custom) {
				const formattedTasks = custom.tasks.map(t => ({
					day: t.day,
					step: t.step,
					assignedTo: t.assignedTo
				}))
				originalTasksRef.current = formattedTasks
				handleInputChange({ target: { name: 'tasks', value: formattedTasks } })
			} else {
				const baseTemplate = templatesOptions.find(t => t._id === state.template)
				if (baseTemplate) {
					const formatted = baseTemplate.steps.map(s => ({
						day: s.day,
						step: s.step,
						assignedTo: s.assignedTo
					}))
					originalTasksRef.current = formatted
					handleInputChange({ target: { name: 'tasks', value: formatted } })
				}
			}
		}
	}, [state.template, state.technician])

	const handleSubmit = (e) => {
		e.preventDefault()

		if (!areTasksEqual(state.tasks, originalTasksRef.current)) {
			Swal.fire({
				showConfirmButton: true,
				showDenyButton: true,
				confirmButtonText: t('global.accept'),
				denyButtonText: t('global.cancel'),
				icon: 'info',
				html: t('admin.template.wasChanged')
			}).then((result) => {
				if (result.isConfirmed) {
					dispatch(updateCustomProtocol(state.template, state.technician._id, state.tasks))
					submit(e)
				}else{
					submit(e)
				}
			})
		}else{
			submit(e)
		}
	}

	function areTasksEqual(a, b) {
		if (a.length !== b.length) return false
		return a.every((task, i) =>
			task.day === b[i].day &&
			task.step === b[i].step &&
			task.assignedTo === b[i].assignedTo
		)
	}

	return (
		<Form onSubmit={handleSubmit} className="px-4 formulario">
			<Row className="my-2">
				<Col xs={12} md={6}>
					<Form.Group>
						<Form.Label>{t('admin.protocol.template')}</Form.Label>
						<Select
							options={getOptions(templatesOptions)}
							value={getOptions(templatesOptions).find(opt => opt.value === state.template?._id)}
							onChange={(option) => handleSelectChange('template', option)}
							required
						/>
					</Form.Group>
				</Col>
				<Col xs={12} md={6}>
					<Form.Group>
						<Form.Label>{t('admin.protocol.startDate')}</Form.Label>
						<Form.Control
							type="date"
							name="startDate"
							value={state.startDate ? new Date(state.startDate).toISOString().split('T')[0] : ''}
							onChange={handleInputChange}
							required
						/>
					</Form.Group>
				</Col>
			</Row>
			<Row className="my-2">
				{protocoltype === 'admin' && (
					<Col xs={12} md={6} className="mt-3">
						<Form.Group>
							<Form.Label>{t('admin.protocol.technician')}</Form.Label>
							<Select
								options={getOptions(techniciansOptions)}
								value={getOptions(techniciansOptions).find(opt => opt.value === state.technician?._id)}
								onChange={(option) => handleSelectChange('technician', option)}
								required
							/>
						</Form.Group>
					</Col>
				)}
				<Col xs={12} md={6} className="mt-3">
					<Form.Group>
						<Form.Label>{t('admin.protocol.breeder')}</Form.Label>
						<Select
							options={getOptions(breedersOptions.filter(u => u.rol === 'breeder'))}
							value={getOptions(breedersOptions).find(opt => opt.value === state.breeder?._id)}
							onChange={(option) => handleSelectChange('breeder', option)}
							required
						/>
					</Form.Group>
				</Col>
			</Row>
			<Row className="my-2">
				<Col xs={12} md={6} className="mt-3">
					<Form.Group>
						<Form.Label>{t('admin.protocol.lots')}</Form.Label>
						<Select
							options={getOptionsLots(lotsOptions)}
							isMulti
							value={getOptionsLots(lotsOptions)?.filter(opt => state.lots?.some(l => l._id === opt.value))}
							onChange={(options) => handleMultiSelectChange('lots', options)}
						/>
					</Form.Group>
				</Col>
				<Col xs={12} md={6} className="mt-3">
					<Form.Group>
						<Form.Label>{t('admin.protocol.animals')}</Form.Label>
						<Select
							options={getOptionsAnimals(animalsOptions)}
							isMulti
							value={getOptionsAnimals(animalsOptions)?.filter(opt => state.animals?.some(a => a === opt.value))}
							onChange={(options) => handleMultiSelectChange('animals', options)}
						/>
					</Form.Group>
				</Col>
			</Row>
			<Row className="mb-2 mt-4 align-items-end">
				<Col xs={2}><Form.Label>{t('admin.template.day')}</Form.Label></Col>
				<Col xs={7}><Form.Label>{t('admin.template.step')}</Form.Label></Col>
				<Col xs={2}><Form.Label>{t('admin.template.assignedTo')}</Form.Label></Col>
				<Col xs={1}></Col>
			</Row>
			{(state.tasks || []).map((task, index) => (
				<Row key={index} className="mb-2 align-items-end">
					<Col xs={2}>
						<Form.Control
							type="number"
							value={task.day}
							onChange={(e) => handleTaskChange(index, 'day', e.target.value)}
							required
						/>
					</Col>
					<Col xs={7}>
						<Form.Control
							type="text"
							value={task.step}
							onChange={(e) => handleTaskChange(index, 'step', e.target.value)}
							required
						/>
					</Col>
					<Col xs={2}>
						<Form.Select
							value={task.assignedTo || 'technical'}
							onChange={(e) => handleTaskChange(index, 'assignedTo', e.target.value)}
						>
							<option value="technical">{t('admin.template.technical')}</option>
							<option value="breeder">{t('admin.template.breeder')}</option>
						</Form.Select>
					</Col>
					<Col xs={1} style={{ height: '40px' }}>
						<Delete size={32} className="me-2 text-danger align-self-center" style={{ cursor: 'pointer' }} onClick={() => removeTask(index)} />
					</Col>
				</Row>
			))}
			<Button variant="secondary" className="mb-3" onClick={addTask}>
				{t('admin.template.addStep')}
			</Button>
			<Row>
				<Col xs={12} lg={6} className="my-2 ms-auto">
					<Button variant='primary' type="submit" className="w-100 my-2">
					{isRegistering ?
						<>
							<span className="spinner-border spinner-border-sm align-middle ms-2"></span>
						</>
					:
						isAddingOrEditing === 'editing' ?
							<>{t('admin.protocol.edit')}</>
						:
							<>{t('admin.protocol.register')}</>
					}
					</Button>
				</Col>
			</Row>
		</Form>
	)
}
